import { Component, EventEmitter, Input, OnChanges, OnInit, Output, Optional } from '@angular/core';
import { ShipmentOrderSpec } from '@core/models/job-file/shipment-order-spec.model';
import { Store, Actions, ofActionSuccessful } from '@ngxs/store';
import { AuthState } from '@core/store/auth/auth.state';
import { JobFileService } from '@core/services/job-file.service';
import { DialogHandlerService } from '@core/services/dialog-handler.service';
import { AlertDialogType } from '@shared/models';
import { filter, switchMap, tap, take, concatMap } from 'rxjs/operators';
import { SetNotificationAction } from '@core/store/notification/notification.actions';
import { LoadJobFile } from '../../../modules/load-management/store/load-management.actions';
import { MatDialogRef } from '@angular/material/dialog';
import { ContainerManagementService } from '../../../modules/wizard/services/container-management.service';
import { SimpleOrderDeletionDialogComponent } from '../../../modules/wizard/components/simple-order-deletion-dialog/simple-order-deletion-dialog.component';
import { SetSpinner } from '@core/store/shared.actions';
import { of } from 'rxjs';

@Component({
  selector: 'hmt-shipment-order-specs',
  templateUrl: './shipment-order-specs.component.html',
  styleUrls: ['./shipment-order-specs.component.scss'],
  standalone: false
})
export class ShipmentOrderSpecsComponent implements OnInit, OnChanges {
  @Input() orders: ShipmentOrderSpec[] = [];
  @Input() selectable = false;
  @Input() deletable = true;
  @Input() showHeader = false;
  @Input() showSearch = false;
  @Input() showRemoveIcon = false;
  @Output() removeShipmentOrder = new EventEmitter<string>();
  @Output() selectOrder = new EventEmitter<{ selected: boolean; order: ShipmentOrderSpec }>();
  @Output() orderRemovedFromContainer = new EventEmitter<string>();

  expandedOrderIds: Set<string> = new Set();
  searchText = '';
  filteredOrders: ShipmentOrderSpec[] = [];

  constructor(
    private store: Store,
    private actions$: Actions,
    private jobFileService: JobFileService,
    private dialogHandlerService: DialogHandlerService,
    private containerManagementService: ContainerManagementService,
    @Optional() private dialogRef: MatDialogRef<ShipmentOrderSpecsComponent>
  ) {}

  ngOnInit(): void {
    this.updateFilteredOrders();
  }

  ngOnChanges(): void {
    this.updateFilteredOrders();
  }

  private updateFilteredOrders(): void {
    this.filteredOrders = [...this.orders];
  }

  onSearchChange(): void {
    const searchLower = this.searchText.toLowerCase().trim();
    
    if (!searchLower) {
      this.filteredOrders = [...this.orders];
    } else {
      this.filteredOrders = this.orders.filter(order =>
        order.orderId?.toLowerCase().includes(searchLower)
      );
    }
  }

  toggleExpand(orderId: string): void {
    if (this.expandedOrderIds.has(orderId)) {
      this.expandedOrderIds.delete(orderId);
    } else {
      this.expandedOrderIds.add(orderId);
    }
  }

  isExpanded(orderId: string): boolean {
    return this.expandedOrderIds.has(orderId);
  }

  onDeleteOrder(order: ShipmentOrderSpec, event: Event): void {
    event.stopPropagation();
    this.removeShipmentOrder.emit(order.id ?? order.orderId);
  }

  onSelectOrder(order: ShipmentOrderSpec, event: { selected: boolean; order: ShipmentOrderSpec }): void {
    this.selectOrder.emit(event);
  }

  onRemoveFromContainer(order: ShipmentOrderSpec): void {
    // Validate required fields
    if (!order.id || !order.orderId || !order.jobRefId || !order.containerId) {
      this.store.dispatch(new SetNotificationAction(
        'Cannot remove order: Missing required information (orderId, jobRefId, or containerId)',
        AlertDialogType.ERROR
      ));
      return;
    }

    // Get orgId from store
    const orgId = this.store.selectSnapshot(AuthState.getOrgId);
    if (!orgId) {
      this.store.dispatch(new SetNotificationAction(
        'Cannot remove order: Organization ID not found',
        AlertDialogType.ERROR
      ));
      return;
    }

    const displayOrderId = order.orderId;

    this.store.dispatch(new SetSpinner(true));
    
    // Check deletion impact first
    this.containerManagementService.checkOrderDeletionImpact(orgId, order.jobRefId!, order.containerId!, order.id)
      .pipe(
        tap(() => this.store.dispatch(new SetSpinner(false))),
        concatMap((impact) => {
          // If no activities will be affected, proceed directly
          if (!impact.activitiesToDelete || impact.activitiesToDelete.length === 0) {
            return of({ action: 'delete-order-only', activitiesToDelete: [] });
          }
          
          // Show dialog to let user decide
          return this.dialogHandlerService.openDialog(
            SimpleOrderDeletionDialogComponent,
            { impact },
            {
              width: '450px',
              maxWidth: '90vw',
              height: 'auto',
              maxHeight: '90vh',
              panelClass: 'simple-deletion-dialog-container',
              disableClose: true,
              position: undefined
            }
          ).afterClosed();
        }),
        filter(result => {
          // Proceed if user chose "No" (delete order only) or "Yes" (delete order + activities)
          // Cancel returns null/undefined, Yes returns 'delete-all', No returns 'delete-order-only'
          return result && (result.action === 'delete-order-only' || result.action === 'delete-all');
        }),
        tap(() => this.store.dispatch(new SetSpinner(true))),
        switchMap((result) => {
          // Determine if we should delete related activities
          const deleteActivities = result.action === 'delete-all';
          
          // Remove order from container activities
          return this.jobFileService.removeOrderFromContainerActivities(
            orgId,
            order.jobRefId!,
            order.containerId!,
            order.orderId,
            deleteActivities
          );
        }), 
        tap(() => {
          // Success - reload job file
          this.store.dispatch(new LoadJobFile(orgId, order.jobRefId!));
          this.store.dispatch(new SetNotificationAction(
            `Order "${displayOrderId}" removed successfully`,
            AlertDialogType.SUCCESS
          ));
        }),
        switchMap(() => {
          // Wait for LoadJobFile action to complete
          return this.actions$.pipe(
            ofActionSuccessful(LoadJobFile),
            take(1)
          );
        }),
        tap(() => {
          this.store.dispatch(new SetSpinner(false));
          // Close the dialog after job file is reloaded
          if (this.dialogRef) {
            this.dialogRef.close();
          }
        }),
        take(1)
      )
      .subscribe({
        error: (error) => {
          console.error('Error removing order from container:', error);
          this.store.dispatch([
            new SetSpinner(false),
            new SetNotificationAction(
              `Failed to remove order "${displayOrderId}"`,
            AlertDialogType.ERROR
            )
          ]);
        }
      });
  }
}

